import { useState, useEffect, useRef, useMemo } from "react";
import { Modal, Spin } from "antd";
import { useOrderingMessage } from "../../useOrderingMessage";
import { LoadingOutlined, CreditCardOutlined, TeamOutlined } from "@ant-design/icons";
import {
  useActivePaymentMethods,
  usePaymentMethods,
} from "../../../../../hooks/usePaymentMethods";
import { useDiscountClients } from "../../../../../hooks/useDiscounts";
import { useCashChangeEnabled } from "../../../../../hooks/useConfiguration";
import { useFiscalInvoiceAvailability } from "../../../../../hooks/useInvoices";
import { useTranslation } from "react-i18next";
import { posModalStyles as modalStyles } from "../../modalPrimitives";
import { useViewport } from "../../../../../hooks/useViewport";
import {
  ModalHeader,
  ModalFooter,
  GhostButton,
  PrimaryButton,
} from "../../modalPrimitives";
import type { BillOut, CloseBillFiscalInvoiceIn } from "../../../../../api/bills";
import { BillCustomerBanner } from "../clients/BillCustomerBanner";
import { BillAmountDiscountBanner } from "../discounts/BillAmountDiscountBanner";
import {
  appliedBillDiscount,
  requestedBillDiscount,
} from "../discounts/billAmountDiscount";
import { useBillDiscountLocked } from "../../../../../hooks/useEdgeMode";
import { formatCents, parseMoneyToCents } from "../../../../../utils/money";
import {
  fiscalInvoiceCardTerminalOf,
  fiscalInvoiceMethodsOf,
} from "../../../../../lib/fiscalInvoice";
import { useBillCloseTip, type RecordedBillTip } from "./useBillCloseTip";
import { billHasActiveRows } from "./tipMath";
import { ClosePaymentBody } from "./ClosePaymentBody";
import { closeTipDueOf, isTipLayout } from "./tipDue";
import {
  recordedBillDepositOf,
  useCloseDeposit,
  type RecordedBillDeposit,
} from "./useCloseDeposit";
import { DepositOfflineStrip } from "./DepositOfflineStrip";
import { FiscalInvoiceBuyerStrip } from "./FiscalInvoiceBuyerStrip";
import { useCloseTableSubmit } from "./useCloseTableSubmit";
import { usePaymentMethodKeys } from "./usePaymentMethodKeys";

interface CloseTableModalProps {
  open: boolean;
  onClose: () => void;
  billId: number;
  tableName: string;
  totalAmount: string;
  operatorName: string;
  canPaymentCash?: boolean;
  canPaymentCard?: boolean;
  canCloseBill1?: boolean;
  customerId?: number | null;
  /** Сметката, която се закрива — за лентата „закачен клиент" под хедъра
   *  (име, процент, спестена сума) и като кутийка в списъка с маси, докато
   *  закриването чака принтера. Без нея лентата не се показва. */
  bill?: BillOut | null;
  // "retry" — сметката вече е закрита, но е без касова бележка (total = 0).
  // Модалът е БУКВАЛНО същият (същите методи, същите три клика, същите
  // текстове); сменя се само endpoint-ът, защото сметката вече е закрита.
  mode?: "close" | "retry";
  /** При „Закриване веднага след поръчка" сервитьорът няма кога да мине през
   *  „Опции → Клиенти" преди закриването — подаден ли е handler-ът, футърът
   *  показва среден бутон „Клиенти", който отваря списъка ВЪРХУ този модал.
   *  Изборът се отразява тук през customerId (сметката се презарежда). */
  onOpenClients?: () => void;
  /** Отворен слой над модала (списъкът с клиенти) — спира Enter/Esc/стрелките
   *  тук, иначе Enter от избора на клиент би закрил сметката. */
  keyboardDisabled?: boolean;
  /** Записаният бакшиш при повторен опит, когато `bill` липсва („Фискализирай"
   *  от „Възстановяване" — сметката идва от историята). Без него — от `bill`. */
  recordedTip?: RecordedBillTip | null;
  /** Записаният депозит при повторен опит, когато `bill` липсва
   *  („Фискализирай" от „Възстановяване"). Без него — от `bill`. */
  recordedDeposit?: RecordedBillDeposit | null;
  /** „Фактуриране" → отворена сметка → купувач: закриването издава фискална
   *  фактура (`fiscalInvoice` в `/close`). Лента с купувача под хедъра; само
   *  методи с фискален слот — без „По сметка", без трите клика; при повторен
   *  опит не важи. Депозитът и бакшишът остават. Без него — старият модал. */
  fiscalInvoiceBuyer?: CloseBillFiscalInvoiceIn | null;
}

export const CloseTableModal = ({
  open,
  onClose,
  billId,
  tableName,
  totalAmount,
  operatorName,
  canPaymentCash = true,
  canPaymentCard = true,
  canCloseBill1 = false,
  customerId = null,
  bill = null,
  mode = "close",
  onOpenClients,
  keyboardDisabled = false,
  recordedTip = null,
  recordedDeposit = null,
  fiscalInvoiceBuyer = null,
}: CloseTableModalProps) => {
  const isRetry = mode === "retry";
  // Повторният опит издава бон за вече закрита сметка — фактура не носи.
  const fiscalInvoice = isRetry ? null : fiscalInvoiceBuyer;
  const isFiscalInvoice = fiscalInvoice !== null;
  const { t, i18n } = useTranslation();
  const { isMobile, isNarrow, isTablet, isDesktop } = useViewport();
  const { data: rawMethods = [], isLoading } = useActivePaymentMethods();
  const { data: allMethods = [] } = usePaymentMethods();
  // Касата кара картов терминал — фискална фактура с карта през него BE още
  // отказва. Отговорът е от кеша: „Фактуриране" го изтегли току-що, преди
  // купувача (без заявка тук); липсва ли (по-старо BE) — модалът е както преди.
  const { data: fiscalAvailability } = useFiscalInvoiceAvailability({ enabled: false });
  const cardTerminal = isFiscalInvoice && fiscalInvoiceCardTerminalOf(fiscalAvailability);
  // id=3 (Смесено) се показва само ако имаме конфигурирани и id=1 (В брой), и id=2 (С карта).
  // Ако backend не е върнал id=3 в активния списък, синтезираме го от мастър списъка.
  // Фискалната фактура — само методите с фискален слот (`lib/fiscalInvoice.ts`),
  // при картов терминал — и без „С карта"/„Смесено" (значи и без бакшиш с карта).
  const methods = useMemo(() => {
    const closable = isFiscalInvoice
      ? fiscalInvoiceMethodsOf(rawMethods, { cardTerminal })
      : rawMethods;
    const hasCash = closable.some((m) => m.id === 1);
    const hasCard = closable.some((m) => m.id === 2);
    const withoutMixed = closable.filter((m) => m.id !== 3);
    if (!hasCash || !hasCard) return withoutMixed;
    // Синтезира се само когато активният списък изобщо го няма: „Смесено",
    // махнато от фискалната фактура (собствен път без бон — „БЕЗ
    // ФИСКАЛИЗИРАНЕ"), не се връща от мастър списъка — BE би го отказал.
    const mixed =
      closable.find((m) => m.id === 3) ??
      (rawMethods.some((m) => m.id === 3)
        ? undefined
        : allMethods.find((m) => m.id === 3));
    if (!mixed) return withoutMixed;
    const cardIdx = withoutMixed.findIndex((m) => m.id === 2);
    const insertAt = cardIdx >= 0 ? cardIdx + 1 : withoutMixed.length;
    return [
      ...withoutMixed.slice(0, insertAt),
      mixed,
      ...withoutMixed.slice(insertAt),
    ];
  }, [rawMethods, allMethods, isFiscalInvoice, cardTerminal]);
  const message = useOrderingMessage();
  // „Ресто при плащане в брой" (Конфигурация → Маркиране): изключено ли е,
  // „В брой" изглежда като „С карта" — само сумата, без банкноти и ресто.
  const cashChangeEnabled = useCashChangeEnabled();

  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<
    number | null
  >(null);
  const [mixedCashAmount, setMixedCashAmount] = useState<number>(0);
  const [mixedCardAmount, setMixedCardAmount] = useState<number>(0);
  const [cashClickCount, setCashClickCount] = useState(0);
  // Полученото за ресто-помощника при „В брой" — чисто визуално, не влиза
  // в payload-а към closeBill.
  const [cashReceived, setCashReceived] = useState("");

  // Депозитът (useCloseDeposit.ts) — закаченият към сметката от „Депозит" →
  // „Използвай депозит", само за четене: методите са само за остатъка
  // („Смесено" го разпределя), без бакшиш и без трите клика. Повторен опит на
  // сметка, платена с депозит, показва записания (`recorded`).
  const recordedBillDeposit = isRetry
    ? (recordedDeposit ?? recordedBillDepositOf(bill))
    : null;
  const closeDeposit = useCloseDeposit({
    isRetry,
    totalAmount,
    bill,
    recordedDeposit: recordedBillDeposit,
  });
  const chosenDeposit = closeDeposit.deposit;
  const depositCoversAll = closeDeposit.coverage?.remainderCents === 0;

  // „Закриване на сметка(1)" (трите клика) е без бон — с фискална фактура не.
  const canCashOnly = canCloseBill1 && !isFiscalInvoice;
  const cashOnlyMode = canCashOnly && cashClickCount >= 3 && !chosenDeposit;
  // Отстъпка в € на цялата сметка в локален режим с каса, която НЕ я умее —
  // „Закрий" чака облака.
  const billDiscountLocked =
    useBillDiscountLocked() && requestedBillDiscount(bill ?? undefined) > 0;

  // Плащане по сметка (id=4) се ПОКАЗВА само при избран клиент с флаг
  // can_pay_on_account (org DB) — без такъв клиент методът е скрит изцяло.
  const { data: discountData } = useDiscountClients(open);
  const hasCustomer = customerId != null;
  // Редовете „Сума без отстъпка / Отстъпка на клиента" над „Общо" в лявата
  // колона — само при закачен клиент (BillTotalsBreakdown крие 0%).
  const clientDiscount = bill?.discount_applied
    ? {
        amount: parseFloat(bill.total_discount || "0") || 0,
        percent: bill.discount_applied.percent ?? bill.discount_percent ?? 0,
        billDiscount: appliedBillDiscount(bill),
      }
    : null;
  const canOnAccount =
    !isFiscalInvoice &&
    hasCustomer &&
    (discountData?.items.find((c) => c.id === customerId)
      ?.can_pay_on_account ??
      false);

  // Със закачен депозит трите клика на „В брой" не важат — нулират се.
  const chosenDepositId = chosenDeposit?.id ?? null;
  useEffect(() => {
    if (chosenDepositId !== null) setCashClickCount(0);
  }, [chosenDepositId]);

  // Ако клиентът отпадне/няма право докато id=4 е избран — върни към cash
  useEffect(() => {
    if (selectedPaymentMethodId === 4 && !canOnAccount) {
      setSelectedPaymentMethodId(1);
    }
  }, [canOnAccount, selectedPaymentMethodId]);

  const methodNameOf = (methodId: number) => {
    const method = allMethods.find((m) => m.id === methodId);
    return (i18n.language === "en" ? method?.name_en : method?.name_bg) ?? "";
  };

  // Бакшишът (payment/useBillCloseTip.ts): при изключени настройки (или
  // по-стар BE) секцията я няма и модалът е буквално старият.
  const closeTip = useBillCloseTip({
    open,
    isRetry,
    orderedAmount: totalAmount,
    activeMethods: rawMethods,
    methodNameOf,
    billMethodId: selectedPaymentMethodId,
    canPaymentCash,
    canPaymentCard,
    canOnAccount,
    cashOnlyMode,
    sendsNonFiscalTotal: cashOnlyMode || selectedPaymentMethodId === 4,
    recordedTip: recordedTip ?? bill,
    // Сметка без нито един ред (празна маса, всичко сторнирано) — бакшиш не
    // може (BE `tip_requires_items`); без `bill` решава BE.
    hasActiveRows: billHasActiveRows(bill),
    hasBillDiscount: requestedBillDiscount(bill ?? undefined) > 0,
    onTipDropped: () => message.warning(t("closeTable.tip.dropped")),
  });

  // Натискането на „Закриване" — проверките и фоновото закриване
  // (`useCloseTableSubmit.ts`); Enter долу вика същото.
  const { isClosing, handleCloseRef, handleClose } = useCloseTableSubmit({
    billId,
    tableName,
    totalAmount,
    bill,
    isRetry,
    selectedPaymentMethodId,
    mixedCashAmount,
    mixedCardAmount,
    cashOnlyMode,
    closeTip,
    closeDeposit,
    recordedBillDeposit,
    fiscalInvoice,
    billDiscountLocked,
    onClose,
  });
  // Кликнат ли е метод ръчно, авто-дефолтът долу спира да пипа избора
  const userPickedMethodRef = useRef(false);

  // Дефолтен метод при отваряне: „По сметка" (4) при избран клиент с правото,
  // иначе „В брой" (1). Данните за клиента идват async → ефектът може да
  // превключи 1 → 4 малко след отварянето, но САМО преди ръчен избор.
  useEffect(() => {
    if (!open || methods.length === 0 || userPickedMethodRef.current) return;
    const defaultMethodId =
      canOnAccount && methods.some((m) => m.id === 4) ? 4 : 1;
    if (
      methods.some((m) => m.id === defaultMethodId) &&
      selectedPaymentMethodId !== defaultMethodId
    ) {
      setSelectedPaymentMethodId(defaultMethodId);
    }
  }, [open, methods, canOnAccount, selectedPaymentMethodId]);

  useEffect(() => {
    if (!open) {
      setSelectedPaymentMethodId(null);
      setMixedCashAmount(0);
      setMixedCardAmount(0);
      setCashClickCount(0);
      setCashReceived("");
      userPickedMethodRef.current = false;
    }
  }, [open]);

  usePaymentMethodKeys({
    enabled: open && isDesktop && !isLoading && !keyboardDisabled,
    // Покрие ли депозитът всичко (или е записан), метод не се избира.
    methodIds:
      depositCoversAll || closeDeposit.recorded
        ? []
        : methods
            .filter((m) => {
              if (m.id === 1) return canPaymentCash;
              if (m.id === 2) return canPaymentCard;
              if (m.id === 3) return canPaymentCash && canPaymentCard;
              if (m.id === 4) return canOnAccount;
              return true;
            })
            .map((m) => m.id),
    selectedMethodId: selectedPaymentMethodId,
    onMove: (methodId) => {
      // Стрелките също са ръчен избор — иначе дефолтът горе го връща веднага.
      userPickedMethodRef.current = true;
      // Смяната на метода със стрелките също прекъсва последователните
      // кликове на „В брой"
      if (methodId !== 1) setCashClickCount(0);
      setSelectedPaymentMethodId(methodId);
    },
    onEnter: () => {
      if (!isClosing && (selectedPaymentMethodId || depositCoversAll || closeDeposit.recorded))
        handleCloseRef.current();
    },
    onEscape: () => {
      if (!isClosing) onClose();
    },
  });

  // Сумите на „Смесено" — винаги до цент: разликата с float (53.5 − 20.1)
  // иначе носи „33.400000000000006" към BE. Със закачен депозит „Смесено"
  // разпределя само остатъка след него.
  const totalCents = parseMoneyToCents(totalAmount) ?? 0;
  const mixedDueCents =
    chosenDeposit && closeDeposit.coverage ? closeDeposit.coverage.remainderCents : totalCents;
  // „Смесено" на широк екран: бутонът е неактивен, докато сборът не покрие
  // сметката (остатъка) — визуално огледало на проверката в handleClose (тя си
  // остава и пази Enter пътя); телефонът остава на старото поведение с предупреждение.
  const mixedIncomplete =
    !isNarrow &&
    selectedPaymentMethodId === 3 &&
    Math.abs(mixedCashAmount + mixedCardAmount - mixedDueCents / 100) > 0.01;
  // Бакшишът е включен (или повторният опит носи записан) — лявата колона е
  // по дизайна „Закриване — всички комбинации" (CloseTipColumn) и е по-широка
  // от методите. Иначе модалът е буквално старият.
  const tipLayout = isTipLayout(closeTip);
  const changeMixedCash = (cash: number) => {
    const cashCents = parseMoneyToCents(cash) ?? 0;
    setMixedCashAmount(cashCents / 100);
    setMixedCardAmount(Math.max(0, mixedDueCents - cashCents) / 100);
  };
  const changeMixedCard = (card: number) => {
    const cardCents = parseMoneyToCents(card) ?? 0;
    setMixedCardAmount(cardCents / 100);
    setMixedCashAmount(Math.max(0, mixedDueCents - cardCents) / 100);
  };
  // „Закриване · 55.00 EUR" — дължимото с метода на сметката (с бакшиша,
  // когато е със същия метод), винаги с два знака.
  // С депозит дължимото е остатъкът след него.
  const closeDueCents =
    closeDeposit.coverage?.remainderCents ??
    closeTipDueOf({
      closeTip,
      orderedCents: totalCents,
      billMethodId: selectedPaymentMethodId,
    }).dueCents;
  // Широк модал на голям екран — двете тонирани колони искат въздух. Покрие
  // ли депозитът всичко, колоната с методите я няма — модалът е по-тесен.
  const width = isMobile ? undefined : isTablet ? 560 : depositCoversAll ? 640 : 880;

  return (
    <Modal
      open={open}
      // Клик по маската по време на проверката на сумата не затваря — иначе
      // закриването би тръгнало след затворен модал, без сервитьорът да
      // е видял дали сумата не се е сменила.
      onCancel={() => {
        if (!isClosing) onClose();
      }}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      width={width}
      styles={modalStyles}
      style={{ maxWidth: "calc(100vw - 32px)" }}
    >
      <div
        className="bg-pos-card font-pos-sans text-pos-ink"
        onClick={() => {
          if (cashOnlyMode) setCashClickCount(0);
        }}
      >
        <ModalHeader
          icon={<CreditCardOutlined />}
          title={t("closeTable.table") + " " + tableName}
          subtitle={`${t("closeTable.operator")} ${operatorName}`}
          onClose={() => {
            if (!isClosing) onClose();
          }}
          isNarrow={isNarrow}
          tone="panel"
        />

        {/* Закачен клиент — лента на цяла ширина под хедъра */}
        {bill?.discount_applied && (
          <BillCustomerBanner
            bill={bill}
            compact={isNarrow}
            // Същият хоризонтален падинг като ModalHeader — иконата и X-ът
            className={isNarrow ? "px-4" : "px-[22px]"}
          />
        )}
        {/* Отстъпка в € на цялата сметка — сумата по-долу е вече без нея */}
        {bill && (
          <BillAmountDiscountBanner
            bill={bill}
            compact={isNarrow}
            className={isNarrow ? "px-4" : "px-[22px]"}
          />
        )}

        {/* Фискална фактура: купувачът, само за четене */}
        {fiscalInvoice && (
          <FiscalInvoiceBuyerStrip
            fiscalInvoice={fiscalInvoice}
            cardTerminal={cardTerminal}
            compact={isNarrow}
            className={isNarrow ? "px-4" : "px-[22px]"}
          />
        )}

        {/* Без интернет закаченият депозит не се приспада — лентата го казва */}
        {closeDeposit.offlineDeposit && (
          <DepositOfflineStrip
            deposit={closeDeposit.offlineDeposit}
            compact={isNarrow}
            className={isNarrow ? "px-4" : "px-[22px]"}
          />
        )}

        {isLoading ? (
          <div className="flex items-center justify-center px-6 py-12">
            <Spin
              indicator={
                <LoadingOutlined className="text-[36px] text-pos-accent" spin />
              }
            />
          </div>
        ) : (
          <>
            <ClosePaymentBody
              closeTip={closeTip}
              tipLayout={tipLayout}
              methods={methods}
              selectedPaymentMethodId={
                closeDeposit.recorded ? closeDeposit.recordedMethodId : selectedPaymentMethodId
              }
              onPickMethod={(methodId, e) => {
                userPickedMethodRef.current = true;
                // Със закачен депозит кликовете на „В брой" не се броят —
                // „Закриване на сметка(1)" с депозит BE отказва.
                if (methodId === 1 && canCashOnly && !chosenDeposit) {
                  e.stopPropagation();
                  setCashClickCount((c) => c + 1);
                } else {
                  // Броят се само ПОСЛЕДОВАТЕЛНИ кликове на „В брой" —
                  // друг метод нулира броенето
                  setCashClickCount(0);
                }
                setSelectedPaymentMethodId(methodId);
              }}
              totalAmount={totalAmount}
              methodNameOf={methodNameOf}
              cashChangeEnabled={cashChangeEnabled}
              cashReceived={cashReceived}
              onCashReceivedChange={setCashReceived}
              clientDiscount={clientDiscount}
              mixedCashAmount={mixedCashAmount}
              mixedCardAmount={mixedCardAmount}
              onMixedCashChange={changeMixedCash}
              onMixedCardChange={changeMixedCard}
              canPaymentCash={canPaymentCash}
              canPaymentCard={canPaymentCard}
              canOnAccount={canOnAccount}
              cashOnlyMode={cashOnlyMode}
              isNarrow={isNarrow}
              disabled={isClosing}
              closeDeposit={closeDeposit}
            />

            <ModalFooter isNarrow={isNarrow} tone="panel" narrowFirst>
              <GhostButton
                label={t("common.cancel")}
                onClick={onClose}
                disabled={isClosing}
                shortcut={isDesktop ? "Esc" : undefined}
              />
              {onOpenClients && (
                <GhostButton
                  icon={<TeamOutlined />}
                  label={t("ordering.clients")}
                  onClick={onOpenClients}
                  disabled={isClosing}
                />
              )}
              <PrimaryButton
                label={
                  depositCoversAll ? (
                    t("closeTable.deposit.closeCovered")
                  ) : (
                    <>
                      {t("closeTable.closeButton")} ·{" "}
                      <span className="font-pos-mono">{formatCents(closeDueCents)} EUR</span>
                    </>
                  )
                }
                onClick={handleClose}
                disabled={
                  billDiscountLocked
                    ? true
                    : closeDeposit.recorded
                    ? false
                    : chosenDeposit
                    ? closeDeposit.blocksZeroBill ||
                      (!depositCoversAll && (!selectedPaymentMethodId || mixedIncomplete))
                    : !selectedPaymentMethodId ||
                      mixedIncomplete ||
                      closeTip.blocksClose
                }
                loading={isClosing}
                shortcut={isDesktop ? "⏎" : undefined}
              />
            </ModalFooter>
          </>
        )}
      </div>
    </Modal>
  );
};
